/**
 * Mention Autocomplete Component
 * Provides @ (data) and # (page) mention autocomplete UI
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  FolderOpen,
  Database,
  FileText,
  Home,
  Type,
  Hash,
  ToggleLeft,
  Calendar,
  List,
  Braces,
  CircleDot,
  AlertCircle,
  X,
} from 'lucide-react';
import {
  mentionSystem,
  MentionContext,
  MentionSegment,
  MENTION_DEBOUNCE_MS,
  addRecentMention,
  clearRecentMentions,
} from '@/services/ai/mentions';

interface MentionAutocompleteProps {
  input: string;
  cursorPosition: number;
  onSelectMention: (reference: string, segment: MentionSegment | null) => void;
  isVisible: boolean;
  /** Type of mention being autocompleted (reserved for future category filtering) */
  mentionType: 'data' | 'page';
  position?: { top: number; left: number };
}

// Icon component mapping
const iconComponents: Record<string, React.ComponentType<{ className?: string }>> = {
  FolderOpen,
  Database,
  FileText,
  Home,
  Type,
  Hash,
  ToggleLeft,
  Calendar,
  List,
  Braces,
  CircleDot,
};

// Category colors for visual distinction
const categoryColors: Record<string, string> = {
  Recent: 'text-yellow-500',
  group: 'text-amber-500',
  schema: 'text-green-500',
  field: 'text-blue-500',
  page: 'text-indigo-500',
};

// Field type to color mapping
const fieldTypeColors: Record<string, string> = {
  string: 'text-blue-500',
  number: 'text-purple-500',
  boolean: 'text-cyan-500',
  date: 'text-pink-500',
  array: 'text-orange-500',
  object: 'text-gray-500',
};

export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  input,
  cursorPosition,
  onSelectMention,
  isVisible,
  mentionType,
  position,
}) => {
  const [suggestions, setSuggestions] = useState<MentionSegment[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below'>('above');
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate optimal dropdown position based on viewport
  useEffect(() => {
    if (!isVisible || !containerRef.current) return;

    const checkPosition = () => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = Math.min(rect.height || 384, 384); // max-h-96 = 384px

      // If dropdown would be cut off at bottom, show above
      // Default is 'above' since autocomplete typically appears above the input
      if (position?.top !== undefined) {
        // If there's not enough space above, show below
        const spaceAbove = rect.top;
        const spaceBelow = viewportHeight - rect.bottom;

        if (spaceAbove < dropdownHeight && spaceBelow > spaceAbove) {
          setDropdownPosition('below');
        } else {
          setDropdownPosition('above');
        }
      }
    };

    checkPosition();
    // Recheck on resize
    window.addEventListener('resize', checkPosition);
    return () => window.removeEventListener('resize', checkPosition);
  }, [isVisible, position, suggestions.length]);

  // Debounced fetch for suggestions
  const fetchSuggestions = useDebouncedCallback(
    async (context: MentionContext) => {
      setIsLoading(true);
      setError(null);

      try {
        const results = await mentionSystem.getMentionSuggestions(context);
        setSuggestions(results);
        setSelectedIndex(0);
      } catch (err) {
        console.error('[MentionAutocomplete] Error fetching suggestions:', err);
        setError(err instanceof Error ? err.message : 'Failed to load suggestions');
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    MENTION_DEBOUNCE_MS
  );

  // Fetch suggestions when input changes
  useEffect(() => {
    if (!isVisible) {
      setSuggestions([]);
      setSelectedIndex(0);
      setError(null);
      return;
    }

    const context = mentionSystem.detectMention(input, cursorPosition);
    if (context) {
      fetchSuggestions(context);
    } else {
      setSuggestions([]);
    }
  }, [input, cursorPosition, isVisible, fetchSuggestions]);

  // Selection handler - defined before keyboard handler
  const handleSelect = useCallback(
    (segment: MentionSegment) => {
      // Track recent mention (only for final selections, not drilling)
      if (!segment.hasChildren) {
        const segmentWithType = { ...segment, type: mentionType };
        addRecentMention(segmentWithType);
      }

      // Determine the suffix based on whether item has children
      const suffix = segment.hasChildren ? '.' : ' ';
      onSelectMention(segment.reference + suffix, segment);
    },
    [onSelectMention, mentionType]
  );

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isVisible) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (suggestions.length > 0) {
            setSelectedIndex(prev => (prev + 1) % suggestions.length);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (suggestions.length > 0) {
            setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
          }
          break;
        case 'Tab':
        case 'Enter':
          if (suggestions.length > 0 && suggestions[selectedIndex]) {
            e.preventDefault();
            handleSelect(suggestions[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onSelectMention('', null);
          break;
      }
    },
    [isVisible, suggestions, selectedIndex, onSelectMention, handleSelect]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (containerRef.current && suggestions.length > 0) {
      const selectedElement = containerRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, suggestions.length]);

  const handleItemClick = (segment: MentionSegment) => {
    handleSelect(segment);
  };

  // Prevent double-firing on mobile
  const handleTouchEnd = (e: React.TouchEvent, segment: MentionSegment) => {
    e.preventDefault();
    handleSelect(segment);
  };

  // Clear recent mentions and refetch suggestions
  const handleClearRecent = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      clearRecentMentions();
      // Refetch suggestions to update the list
      const context = mentionSystem.detectMention(input, cursorPosition);
      if (context) {
        fetchSuggestions(context);
      }
    },
    [input, cursorPosition, fetchSuggestions]
  );

  // Get positioning styles for dropdown with viewport-awareness
  const getPositionStyles = useCallback((): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      left: position?.left || 0,
      minWidth: '250px',
      maxWidth: Math.min(400, typeof window !== 'undefined' ? window.innerWidth - 32 : 400),
    };

    if (dropdownPosition === 'below') {
      return {
        ...baseStyles,
        top: position ? `calc(100% + ${position.top}px)` : 'calc(100% + 8px)',
      };
    }

    // Default: above
    return {
      ...baseStyles,
      bottom: position ? `calc(100% + ${position.top}px)` : 'calc(100% + 8px)',
    };
  }, [position, dropdownPosition]);

  // Get icon component for a segment
  const getIcon = (segment: MentionSegment) => {
    const iconName = segment.icon || 'CircleDot';
    const IconComponent = iconComponents[iconName];

    if (!IconComponent) {
      return <CircleDot className="w-4 h-4" />;
    }

    // Determine color based on category and field type
    let colorClass = categoryColors[segment.category] || 'text-gray-400';
    if (segment.category === 'field' && segment.metadata?.fieldType) {
      colorClass = fieldTypeColors[segment.metadata.fieldType] || colorClass;
    }
    if (segment.category === 'page' && segment.metadata?.isDefaultPage) {
      colorClass = 'text-green-500';
    }

    return <IconComponent className={`w-4 h-4 ${colorClass}`} />;
  };

  // Group suggestions by category
  const groupedSuggestions = suggestions.reduce(
    (acc, segment) => {
      const category = segment.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(segment);
      return acc;
    },
    {} as Record<string, MentionSegment[]>
  );

  // Category order for display
  const categoryOrder = ['Recent', 'group', 'schema', 'field', 'page'];
  const sortedCategories = Object.keys(groupedSuggestions).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
  );

  // Get flattened index for keyboard navigation
  const getFlatIndex = (category: string, indexInCategory: number): number => {
    let flatIndex = 0;
    for (const cat of sortedCategories) {
      if (cat === category) {
        return flatIndex + indexInCategory;
      }
      flatIndex += groupedSuggestions[cat].length;
    }
    return flatIndex;
  };

  if (!isVisible) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div
        ref={containerRef}
        role="listbox"
        aria-label="Loading mention suggestions"
        aria-busy="true"
        className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
        style={getPositionStyles()}
      >
        <div className="p-3 space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-1" />
                <div className="h-3 bg-gray-100 dark:bg-gray-600 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        ref={containerRef}
        role="alert"
        aria-label={`Error loading suggestions: ${error}`}
        className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
        style={getPositionStyles()}
      >
        <div className="p-3 flex items-center gap-2 text-red-500">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (suggestions.length === 0) {
    return (
      <div
        ref={containerRef}
        role="listbox"
        aria-label={mentionType === 'data' ? 'No schemas or fields found' : 'No pages found'}
        className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
        style={getPositionStyles()}
      >
        <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">
          {mentionType === 'data' ? 'No schemas or fields found' : 'No pages found'}
        </div>
      </div>
    );
  }

  // Category display names
  const categoryLabels: Record<string, string> = {
    Recent: 'Recent',
    group: 'Groups',
    schema: 'Schemas',
    field: 'Fields',
    page: 'Pages',
  };

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-label={`${mentionType === 'data' ? 'Data' : 'Page'} mention suggestions`}
      aria-activedescendant={suggestions[selectedIndex] ? `mention-option-${suggestions[selectedIndex].id}` : undefined}
      className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto mention-autocomplete-scroll"
      style={{
        bottom: position ? `calc(100% + ${position.top}px)` : 'calc(100% + 8px)',
        left: position?.left || 0,
        minWidth: '250px',
        maxWidth: '400px',
      }}
    >
      <div className="p-2">
        {sortedCategories.map(category => (
          <div key={category} role="group" aria-label={categoryLabels[category] || category}>
            {/* Category header */}
            <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky top-0 bg-white dark:bg-gray-800 flex items-center justify-between">
              <span>{categoryLabels[category] || category}</span>
              {category === 'Recent' && (
                <button
                  type="button"
                  onClick={handleClearRecent}
                  className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  aria-label="Clear recent mentions"
                  title="Clear recent"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Category items */}
            {groupedSuggestions[category].map((segment, indexInCategory) => {
              const flatIndex = getFlatIndex(category, indexInCategory);
              const isSelected = selectedIndex === flatIndex;

              return (
                <div
                  key={segment.id}
                  id={`mention-option-${segment.id}`}
                  role="option"
                  aria-selected={isSelected}
                  data-index={flatIndex}
                  className={`px-3 py-2 rounded cursor-pointer transition-colors flex items-center gap-3 ${
                    isSelected
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                  onClick={() => handleItemClick(segment)}
                  onTouchEnd={e => handleTouchEnd(e, segment)}
                >
                  {/* Icon */}
                  <span className="flex-shrink-0" aria-hidden="true">{getIcon(segment)}</span>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{segment.value}</span>
                      {segment.hasChildren && (
                        <span className="text-xs text-gray-400" aria-label="has more options">...</span>
                      )}
                    </div>
                    {segment.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {segment.description}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer hint */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 text-xs text-gray-500 dark:text-gray-400" aria-hidden="true">
        <span className="font-medium">Tab/Enter</span> to select •{' '}
        <span className="font-medium">↑↓</span> to navigate •{' '}
        <span className="font-medium">Esc</span> to close
      </div>
    </div>
  );
};
